import { useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Plus, Minus } from "lucide-react";
import { PageShell } from "../components/layout/PageShell";
import { LeadModal } from "../components/shared/LeadCapture";
import { DOORWAY_PAGES } from "../data/doorwayContent";

// PRIORITY 3 — the deeper Doorway pages (§45–§52).
// One component, five doorways. §45 fixes the section order for all of them:
//   emotional/problem hero → promise → who this is for → what it actually is →
//   why it's different → relevant credibility → proof → what happens → objections → CTA
// Every page ends on a conversation or application — never a direct-purchase CTA (§48).

// Rendered as h2 rather than p: these introduce the §45 sections, so they are the
// page's real second-level headings. Keeps h1 -> h2 -> h3 intact for screen readers.
const Eyebrow = ({ children }) => (
  <h2 className="text-sm tracking-[0.24em] uppercase text-[#E7CC86]/80 mb-4 font-sans font-normal">{children}</h2>
);

export default function WorkDetail() {
  const { key } = useParams();
  const d = DOORWAY_PAGES[key];
  const [leadOpen, setLeadOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  if (!d) return <Navigate to="/work" replace />;

  const openLead = () => setLeadOpen(true);

  return (
    <PageShell>
      {/* 1 · EMOTIONAL / PROBLEM-ORIENTED HERO */}
      <section data-testid={`doorway-page-${d.id}`} className="relative pt-32 md:pt-40 pb-16 md:pb-20 overflow-hidden grain">
        <div className="aurora-weave" />
        <div className="absolute inset-0 hex-texture opacity-40 pointer-events-none" />
        <div className="absolute -top-40 right-0 w-[55rem] h-[55rem] radial-glow blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-6xl mx-auto px-5 md:px-8 grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16 items-center">
          <div>
            <p className="flex items-center gap-3 text-sm tracking-[0.24em] uppercase text-[#E7CC86]/80 mb-5">
              <span className="font-display text-2xl text-[#E7CC86]/60">{d.n}</span> {d.doorway}
            </p>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-[3.6rem] leading-[1.06] text-cream">{d.hero.h}</h1>
            <p className="font-serif italic text-xl md:text-2xl text-gold mt-6 max-w-xl">{d.hero.sub}</p>
            <div className="mt-9">
              <button data-testid="doorway-hero-cta" onClick={openLead}
                className="btn-gold px-8 py-4 rounded-full text-base font-semibold inline-flex items-center gap-2">
                {d.cta.label} <ArrowRight size={18} />
              </button>
            </div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9 }}
            className="relative rounded-3xl overflow-hidden ring-gold-frame aspect-[4/5] hidden lg:block">
            <img src={d.img} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#080B12] via-transparent to-transparent" />
          </motion.div>
        </div>
      </section>

      {/* 2 · THE PROMISE */}
      <section className="relative py-16 md:py-24 bg-[#0B1020] overflow-hidden">
        <div className="line-weave" />
        <div className="relative z-10 max-w-3xl mx-auto px-5 md:px-8">
          <p className="text-xl md:text-2xl text-cream/85 leading-relaxed">{d.promise}</p>
        </div>
      </section>

      {/* 3 · WHO THIS IS FOR  ·  4 · WHAT IT ACTUALLY IS */}
      <section className="relative py-16 md:py-24 bg-[#080B12] overflow-hidden grain">
        <div className="aurora-weave slow" />
        <div className="relative z-10 max-w-6xl mx-auto px-5 md:px-8 grid lg:grid-cols-[0.8fr_1.2fr] gap-12 lg:gap-16">
          <div>
            <Eyebrow>{d.who.h}</Eyebrow>
            <ul className="space-y-3.5 mt-6">
              {d.who.items.map((w) => (
                <li key={w} className="flex items-start gap-3 text-cream/75 leading-snug">
                  <Check size={17} className="text-[#E7CC86] shrink-0 mt-1" /> {w}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <Eyebrow>{d.experience.h}</Eyebrow>
            <p className="text-lg text-cream/72 leading-relaxed mb-8">{d.experience.body}</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {d.experience.blocks.map((b, i) => (
                <motion.div key={b.t} data-testid={`doorway-block-${i}`}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                  className="glass rounded-2xl p-6">
                  <h3 className="font-display text-lg text-cream leading-tight">{b.t}</h3>
                  <p className="text-cream/55 text-sm mt-2 leading-relaxed">{b.d}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 5 · WHY THIS IS DIFFERENT  ·  6 · RELEVANT CREDIBILITY */}
      <section className="relative py-16 md:py-24 bg-[#0B1020] overflow-hidden">
        <div className="line-weave" />
        <div className="relative z-10 max-w-5xl mx-auto px-5 md:px-8 grid md:grid-cols-2 gap-12 md:gap-16">
          <div>
            <Eyebrow>{d.different.h}</Eyebrow>
            <p className="text-lg text-cream/72 leading-relaxed">{d.different.body}</p>
          </div>
          <div className="md:border-l md:border-[#C9A24B]/20 md:pl-16">
            <Eyebrow>Why Gary</Eyebrow>
            <p className="text-lg text-cream/72 leading-relaxed">{d.credibility}</p>
          </div>
        </div>
      </section>

      {/* 7 · PROOF */}
      <section className="relative py-16 md:py-24 bg-[#080B12] overflow-hidden grain">
        <div className="aurora-weave slow" />
        <div className="relative z-10 max-w-3xl mx-auto px-5 md:px-8 text-center">
          <figure data-testid="doorway-proof">
            <blockquote className="font-serif italic text-xl md:text-2xl text-cream/85 leading-relaxed">“{d.proof.quote}”</blockquote>
            <figcaption className="mt-6">
              <span className="block font-semibold text-[#E7CC86]">{d.proof.name}</span>
              <span className="block text-sm text-cream/50 mt-0.5">{d.proof.title}</span>
            </figcaption>
          </figure>
        </div>
      </section>

      {/* 8 · WHAT HAPPENS */}
      <section className="relative py-16 md:py-24 bg-[#0B1020] overflow-hidden">
        <div className="line-weave" />
        <div className="relative z-10 max-w-6xl mx-auto px-5 md:px-8">
          <div className="max-w-2xl mb-12">
            <Eyebrow>{d.process.h}</Eyebrow>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {d.process.steps.map((s, i) => (
              <motion.div key={s.n} data-testid={`doorway-step-${i}`}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }}
                className="glass rounded-2xl p-6">
                <span className="font-display text-2xl text-[#E7CC86]/60">{s.n}</span>
                <h3 className="font-display text-xl text-cream mt-2 leading-tight">{s.t}</h3>
                <p className="text-cream/55 text-sm mt-2 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 9 · OBJECTIONS */}
      <section className="relative py-16 md:py-24 bg-[#080B12] overflow-hidden grain">
        <div className="relative z-10 max-w-3xl mx-auto px-5 md:px-8">
          <div className="mb-9">
            <Eyebrow>Questions</Eyebrow>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl text-cream">Before you reach out.</h2>
          </div>
          <div className="space-y-3">
            {d.faq.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={i} data-testid={`doorway-faq-${i}`} className="glass rounded-2xl overflow-hidden">
                  <button onClick={() => setOpenFaq(open ? -1 : i)} className="w-full flex items-center justify-between gap-4 text-left px-6 py-5">
                    <span className="font-display text-lg text-cream">{f.q}</span>
                    {open ? <Minus size={20} className="text-[#E7CC86] shrink-0" /> : <Plus size={20} className="text-[#E7CC86] shrink-0" />}
                  </button>
                  {open && <p className="px-6 pb-6 -mt-1 text-cream/65 leading-relaxed">{f.a}</p>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 10 · CLEAR CTA — conversation or application, never direct purchase (§48) */}
      <section className="relative py-20 md:py-28 bg-[#0B1020] overflow-hidden grain">
        <div className="aurora-weave" />
        <div className="absolute inset-0 hex-texture opacity-40 pointer-events-none" />
        <div className="relative z-10 max-w-2xl mx-auto px-5 md:px-8 text-center">
          <h2 className="font-display font-semibold text-4xl sm:text-5xl text-cream mb-5 leading-[1.1]">{d.cta.h}</h2>
          <p className="text-lg text-cream/65 mb-9">{d.cta.body}</p>
          <button data-testid="doorway-final-cta" onClick={openLead}
            className="btn-gold px-9 py-4 rounded-full text-lg font-semibold inline-flex items-center gap-2">
            {d.cta.label} <ArrowRight size={20} />
          </button>
          <div className="mt-12">
            <Link to="/work" data-testid="doorway-back" className="inline-flex items-center gap-2 text-cream/50 hover:text-[#E7CC86] transition-colors text-sm">
              <ArrowLeft size={16} /> Explore the doorways
            </Link>
          </div>
        </div>
      </section>

      <LeadModal
        open={leadOpen}
        onClose={() => setLeadOpen(false)}
        title={d.doorway}
        subtitle={d.cta.label}
        source={`doorway-${d.id}`}
        avatar="community"
        pathway={d.id}
        withMessage
        cta={d.cta.label}
      />
    </PageShell>
  );
}
